/**
 * @fileoverview WIP POP-specific mixins
 *
 * Should eventually be the root of all pop-base-*
 * @rally https://rally1.rallydev.com/#/?detail=/userstory/816362881827&fdp=true
 */

import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { DigitalSharedAnalytics } from '@trp-dms-digital-shared/ds-ui-library-common/src/utils/analytics/analytics.js';
import { ComponentLoaderMixin } from '@trp-dms-digital-shared/ds-ui-library-components/src/lib/component-loader/component-loader.js';

import { Constants } from '../base.constants';
import { PopConfiguration } from '../../common/configuration/pop-configuration';
import {
  TTrackingHTMLElement,
  PopBaseMixinInterface,
  Constructor,
} from './types';

/**
 * The base mixin for making a POP component
 */
export const PopBaseMixin = <T extends Constructor<LitElement>>(
  superClass: T,
): T & Constructor<PopBaseMixinInterface> => {
  class PopBaseMixed extends superClass {
    private _tagName = '';
    /**
     * @internal
     */
    @state()
    initialized = false;

    /**
     * @internal
     */
    popConfiguration: PopConfiguration = new PopConfiguration();

    /**
     * The current version of the pop component, used for version specific scoping
     * @internal
     */
    @property({ attribute: Constants.POP_VERSION, reflect: true })
    version = Constants.VERSION;

    constructor(...args: any[]) {
      super(...args);
    }

    /**
     * Ensure new tagName content and set the _tagName variable
     */
    set tagName(newTagName: string) {
      if (!newTagName || newTagName === '') {
        return;
      }
      this._tagName = newTagName;
    }

    /**
     * Get the tagName content
     */
    get tagName() {
      return this._tagName;
    }

    /**
     * If the component is disconnected from the DOM, remove the analytics event listeners.
     */
    public override disconnectedCallback() {
      DigitalSharedAnalytics.unsetHostEventHandlerForAnalytics(
        this as unknown as TTrackingHTMLElement,
      );
      super.disconnectedCallback();
    }

    /*
     * This method is called once any Defer conditions (from Component Loader Mixin)
     * are met. Override this method to add UI styles
     * Wrapper to be used inside of the `render()` method
     * @example
     * render() {
     *  return this.renderComponent(html`<p>meow</p>`)
     * }
     */
    renderComponent(content: unknown) {
      return !this.initialized
        ? html`<!--- Not Initialized --->`
        : html`${content}`;
    }

    updated() {
      DigitalSharedAnalytics.unsetHostEventHandlerForAnalytics(
        this as unknown as TTrackingHTMLElement,
      );

      DigitalSharedAnalytics.setHostEventHandlerForAnalytics(
        this as unknown as TTrackingHTMLElement,
        Constants.POP_VERSION,
        this.version,
      );
    }

    render() {
      return this.renderComponent(
        html`<p>You must override PopBase's render method</p>`,
      );
    }
  }
  return PopBaseMixed;
};

/**
 * WIP extendable base component with POP-required properties
 */
export class PopBase extends PopBaseMixin(ComponentLoaderMixin(LitElement)) {}
